import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

interface RouteConfig {
  Android?: string;
  iOS?: string;
  Desktop?: string;
  default?: string;
  fallback?: string;
  androidFallback?: string;
  iOSFallback?: string;
  eventName?: string;
  androidIntent?: boolean;
}

interface RedirectConfig {
  [path: string]: RouteConfig;
}

interface DynamicRedirectProps {
  config: RedirectConfig;
  pathKey?: string;
  pathParams?: Record<string, string | undefined>;
}

const DynamicRedirect: React.FC<DynamicRedirectProps> = ({
  config,
  pathKey,
  pathParams = {},
}) => {
  const [showAppMessage, setShowAppMessage] = useState<boolean>(false);
  const [device, setDevice] = useState<string>("");
  const location = useLocation();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const utm_campaign = queryParams.get("utm_campaign") || "";
    const utm_source = queryParams.get("utm_source") || "";
    const utm_medium = queryParams.get("utm_medium") || "";
    const tab = queryParams.get("tab") || "";
    const campaign = utm_campaign || utm_source || utm_medium || "";

    // Detect device type
    const userAgent =
      navigator.userAgent || navigator.vendor || (window as any).opera;
    let deviceType = "Desktop";
    if (/android/i.test(userAgent)) {
      deviceType = "Android";
    } else if (/iPhone|iPad|iPod/i.test(userAgent)) {
      deviceType = "iOS";
    }
    setDevice(deviceType);

    const configKey = pathKey || location.pathname;
    const routeConfig = config[configKey] || {};

    const eventName = routeConfig.eventName || "marketing_redirect";

    let redirectUrl: string =
      (routeConfig[deviceType as keyof RouteConfig] as string) ||
      (routeConfig.default as string) ||
      "";

    // Replace path parameters in the URL
    if (pathParams) {
      for (const [key, value] of Object.entries(pathParams)) {
        if (value !== undefined) {
          redirectUrl = redirectUrl.replace(
            new RegExp(`\\{${key}\\}`, "g"),
            value,
          );
        }
      }
    }

    // Handle Android Intent URLs differently from other URLs
    if (
      redirectUrl &&
      deviceType === "Android" &&
      routeConfig.androidIntent === true
    ) {
      // For Android Intent URLs, we need to handle them differently
      // We'll add any necessary query parameters to the Intent URL format
      const intentComponents = redirectUrl.split(";");
      const baseIntent = intentComponents[0];
      const updatedComponents = [baseIntent];

      // Add campaign and other parameters as extras in the Intent format
      if (campaign) {
        updatedComponents.push(`S.campaign=${encodeURIComponent(campaign)}`);
      }

      if (tab) {
        updatedComponents.push(`S.tab=${encodeURIComponent(tab)}`);
      }

      // Add any other query parameters
      queryParams.forEach((value, key) => {
        if (
          !["utm_campaign", "utm_source", "utm_medium", "tab"].includes(key)
        ) {
          updatedComponents.push(`S.${key}=${encodeURIComponent(value)}`);
        }
      });

      // Add back any remaining components from the original Intent URL
      for (let i = 1; i < intentComponents.length; i++) {
        if (!intentComponents[i].startsWith("S.")) {
          updatedComponents.push(intentComponents[i]);
        }
      }

      redirectUrl = updatedComponents.join(";");
    } else if (redirectUrl) {
      try {
        // Handle iOS custom URL schemes
        if (deviceType === "iOS" && redirectUrl.startsWith("esemie://")) {
          // For iOS custom schemes, append parameters as query string
          const [baseUrl, existingParams] = redirectUrl.split("?");
          const urlParams = new URLSearchParams(existingParams || "");

          if (campaign) {
            urlParams.append("campaign", campaign);
          }
          if (tab) {
            urlParams.append("tab", tab);
          }

          queryParams.forEach((value, key) => {
            if (
              !["utm_campaign", "utm_source", "utm_medium", "tab"].includes(key)
            ) {
              urlParams.append(key, value);
            }
          });

          redirectUrl = `${baseUrl}${urlParams.toString() ? "?" + urlParams.toString() : ""}`;
        } else if (redirectUrl.startsWith("http")) {
          const url = new URL(redirectUrl);
          if (campaign) {
            url.searchParams.append("campaign", campaign);
          }

          if (tab) {
            url.searchParams.append("tab", tab);
          }

          queryParams.forEach((value, key) => {
            if (
              !["utm_campaign", "utm_source", "utm_medium", "tab"].includes(key)
            ) {
              url.searchParams.append(key, value);
            }
          });

          redirectUrl = url.toString();
        } else if (
          redirectUrl.includes("://") &&
          !redirectUrl.includes("#Intent")
        ) {
          const paramString = [];
          if (campaign)
            paramString.push(`campaign=${encodeURIComponent(campaign)}`);
          if (tab) paramString.push(`tab=${encodeURIComponent(tab)}`);

          queryParams.forEach((value, key) => {
            if (
              !["utm_campaign", "utm_source", "utm_medium", "tab"].includes(key)
            ) {
              paramString.push(
                `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
              );
            }
          });

          redirectUrl = `${redirectUrl}${paramString.length > 0 ? "?" + paramString.join("&") : ""}`;
        }
      } catch (e) {
        console.error("Error processing URL:", e);
      }
    }

    // Push to dataLayer
    // GTM event push
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "invoice_create",
      device,
      redirectUrl,
      utm_campaign,
      utm_source,
      utm_medium,
      timestamp: new Date().toISOString(),
    });

    // Handle redirects based on URL type and device
    if (deviceType === "Android") {
      // Try to open the app first
      window.location.href = redirectUrl;

      // Set up a fallback timer that will redirect to Play Store if app doesn't open
      const fallbackTimeout = setTimeout(() => {
        if (routeConfig.androidFallback) {
          setShowAppMessage(true);
          window.location.href = routeConfig.androidFallback;
        }
      }, 2000);

      return () => clearTimeout(fallbackTimeout);
    } else if (deviceType === "iOS") {
      console.log("iOS redirectUrl:", redirectUrl);

      // Create a hidden iframe to attempt the app deep link
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = redirectUrl;
      document.body.appendChild(iframe);

      // Set up fallback timer
      const fallbackTimeout = setTimeout(() => {
        document.body.removeChild(iframe);
        if (routeConfig.iOSFallback) {
          setShowAppMessage(true);
          // Use window.open for App Store links to ensure they work properly
          window.location.assign(routeConfig.iOSFallback);
        }
      }, 2500); // Reduced timeout for better UX

      // Listen for page visibility changes to detect if app opened
      const handleVisibilityChange = () => {
        if (document.hidden) {
          // User likely switched to the app, clear the fallback
          clearTimeout(fallbackTimeout);
          document.removeEventListener(
            "visibilitychange",
            handleVisibilityChange,
          );
        }
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);

      // Also listen for page focus/blur as backup
      const handleBlur = () => {
        clearTimeout(fallbackTimeout);
        window.removeEventListener("blur", handleBlur);
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange,
        );
      };

      window.addEventListener("blur", handleBlur);

      return () => {
        clearTimeout(fallbackTimeout);
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange,
        );
        window.removeEventListener("blur", handleBlur);
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
      };
    } else {
      // For Desktop, just redirect directly
      const redirectTimeout = setTimeout(() => {
        window.location.href = redirectUrl;
      }, 2000);

      return () => clearTimeout(redirectTimeout);
    }
  }, [config, pathKey, location]);

  return (
    <div className="h-screen flex justify-center items-center p-4">
      <p className="text-center lg:text-xl md:text-lg text-base">
        {showAppMessage ? (
          <>
            Opening {device === "iOS" ? "App Store" : "Google Play Store"}...
            <br />
            <span className="text-sm text-gray-600 mt-2 block">
              If the app is already installed, please check if it opened in the
              background.
            </span>
          </>
        ) : (
          <>
            {device === "iOS" ? "Opening app" : "Redirecting"}...
            <br />
            <span className="text-sm text-gray-600">Please wait</span>
          </>
        )}
      </p>
    </div>
  );
};

export default DynamicRedirect;
